import { useEffect, useRef, useState, type ReactElement } from "react";
import { Group, Path, Rect, Text } from "react-konva";
import type {
  CollaboratorState,
  PresenceCanvasView,
} from "../../lib/sharedRoomTypes";
import type { FitInfo } from "../../lib/useFitToRoom";
import type { Vec2 } from "../../lib/types";
import { chromeScale } from "../../lib/canvasLabels";

/**
 * Konva overlay that renders other users' cursors at 60 Hz, smoothly
 * interpolating between the ~30 Hz wire updates we get from RTDB. This
 * matches Figma's cursor-rendering technique (Sujeet Jaiswal's writeup):
 * receiver-side rAF interpolation between the last two samples is what
 * keeps cursors looking buttery even though the network rate is half
 * that.
 *
 * Cursors come in room-coordinate space; we convert to pixel space here
 * via the same `useFitToRoom` instance the canvas uses, so cursors stay
 * stable under zoom/pan without any extra round-trips.
 *
 * When a cursor falls outside the visible viewport (collaborator is
 * looking at a different part of the room), we render a Figma-style
 * "edge indicator" pill at the nearest viewport edge so the local user
 * always knows where their collaborators are working.
 */

interface Props {
  collaborators: ReadonlyMap<string, CollaboratorState>;
  fit: FitInfo;
  roomPoints: Vec2[];
  view: PresenceCanvasView;
  /** Wall index displayed in the wall view; null elsewhere. Only cursors
   *  whose own `cursor.wallIndex` matches will be drawn. */
  activeWallIndex: number | null;
  /**
   * Effective wall length in inches (only relevant in `wall` view). When
   * non-null, cursor x is treated as "along wall in inches" and y as
   * "from floor in inches". The wall canvas uses 0 in inches at the wall
   * start; null elsewhere.
   */
  wallLengthIn: number | null;
  /** Viewport size in CSS px. Used to project off-screen cursors onto
   *  the nearest edge as a Figma-style indicator pill. */
  viewport: { width: number; height: number };
  /** Stage transform: maps room-px (after fit) to viewport-px. Pass
   *  `{ scale, x, y }` from `useStagePanZoom`. */
  stage: { scale: number; x: number; y: number };
}

interface CursorAnim {
  /** Last sample we received from RTDB. */
  target: Vec2;
  /** Interpolated cursor position used for rendering. */
  current: Vec2;
  /** Time of the last `target` change (perf clock). */
  lastChangeAt: number;
}

const INTERPOLATION_DURATION_MS = 80;

export function CollaboratorCursors({
  collaborators,
  fit,
  roomPoints,
  view,
  activeWallIndex,
  viewport,
  stage,
}: Props) {
  // Animation state lives in a ref so the rAF loop can mutate it
  // without triggering a render every frame; we then bump a tick
  // counter to force a re-render at the rAF cadence.
  const animRef = useRef<Map<string, CursorAnim>>(new Map());
  const [, forceRender] = useState(0);

  // Update animation targets when collaborators change.
  useEffect(() => {
    const next = new Map<string, CursorAnim>();
    const now =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    for (const [uid, c] of collaborators.entries()) {
      if (!c.cursor) continue;
      const target: Vec2 = { x: c.cursor.x, y: c.cursor.y };
      const prior = animRef.current.get(uid);
      if (prior) {
        // Snap the current sample if the cursor jumped (e.g. wall switch).
        // Otherwise keep the in-flight current position.
        const dx = target.x - prior.target.x;
        const dy = target.y - prior.target.y;
        const distSquared = dx * dx + dy * dy;
        const tooFar = distSquared > 1e6; // arbitrary; ~1000 inch jump
        next.set(uid, {
          target,
          current: tooFar ? target : prior.current,
          lastChangeAt: now,
        });
      } else {
        next.set(uid, { target, current: target, lastChangeAt: now });
      }
    }
    animRef.current = next;
    // Trigger one render so the new cursor renders on its first frame
    // even if rAF hasn't ticked yet.
    forceRender((x) => x + 1);
  }, [collaborators]);

  // rAF loop: tween `current` toward `target` at 60Hz. Runs only while
  // there is at least one cursor to animate.
  useEffect(() => {
    if (animRef.current.size === 0) return;
    let raf = 0;
    function tick() {
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      let stillAnimating = false;
      for (const anim of animRef.current.values()) {
        const t = Math.min(
          1,
          (now - anim.lastChangeAt) / INTERPOLATION_DURATION_MS
        );
        // EaseOutCubic — fast at the start, soft at the end. Matches the
        // sense of "cursor lands" rather than "cursor coasts".
        const e = 1 - Math.pow(1 - t, 3);
        anim.current = {
          x: anim.current.x + (anim.target.x - anim.current.x) * e,
          y: anim.current.y + (anim.target.y - anim.current.y) * e,
        };
        const dx = anim.target.x - anim.current.x;
        const dy = anim.target.y - anim.current.y;
        if (Math.abs(dx) > 0.01 || Math.abs(dy) > 0.01) stillAnimating = true;
      }
      forceRender((x) => x + 1);
      if (stillAnimating) raf = requestAnimationFrame(tick);
    }
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  });

  if (animRef.current.size === 0) return null;

  const cursors: ReactElement[] = [];
  // Edge-indicator margin so the pill never sits flush against the
  // viewport corners; matches the room-pill padding for visual unity.
  const EDGE_PAD = 12;
  for (const [uid, c] of collaborators.entries()) {
    if (!c.cursor) continue;
    if (c.cursor.view !== view) continue;
    if (view === "wall" && c.cursor.wallIndex !== activeWallIndex) continue;
    const anim = animRef.current.get(uid);
    if (!anim) continue;
    // We don't gate on `roomPoints` membership — a cursor briefly
    // outside the polygon (e.g. while pulling a piece toward a wall) is
    // legitimate and we'd rather show it than hide it.
    void roomPoints;
    const sceneFitPx = fit.pointToPx(anim.current);
    // Convert from the stage's pre-transform "scene" px into the
    // viewport's CSS px space. Konva applies the same transform to its
    // own children, but the off-screen check below needs viewport coords.
    const viewportX = sceneFitPx.x * stage.scale + stage.x;
    const viewportY = sceneFitPx.y * stage.scale + stage.y;
    const onScreen =
      viewportX >= 0 &&
      viewportX <= viewport.width &&
      viewportY >= 0 &&
      viewportY <= viewport.height;
    if (onScreen) {
      cursors.push(
        <CursorMarker
          key={uid}
          x={sceneFitPx.x}
          y={sceneFitPx.y}
          scale={chromeScale(stage.scale)}
          color={c.color}
          name={c.displayName}
          // The selection halo already carries the name pill; one label per person.
          showLabel={!hasSelectionPill(c.selection)}
        />
      );
      continue;
    }
    // Off-screen: drop a Figma-style edge indicator at the viewport
    // boundary pointing toward the collaborator. We project the line
    // from the viewport center to the cursor onto the viewport rect.
    const cx = viewport.width / 2;
    const cy = viewport.height / 2;
    const dx = viewportX - cx;
    const dy = viewportY - cy;
    if (dx === 0 && dy === 0) continue; // safety
    const tX = dx === 0 ? Infinity : (cx - EDGE_PAD) / Math.abs(dx);
    const tY = dy === 0 ? Infinity : (cy - EDGE_PAD) / Math.abs(dy);
    const t = Math.max(0.0001, Math.min(tX, tY));
    const edgeViewportX = cx + dx * t;
    const edgeViewportY = cy + dy * t;
    // Convert the viewport-space edge position back into scene-space
    // so it renders correctly inside the stage's transformed layer.
    const edgeSceneX = (edgeViewportX - stage.x) / stage.scale;
    const edgeSceneY = (edgeViewportY - stage.y) / stage.scale;
    // The pill needs to render at constant CSS size regardless of the
    // stage scale — counter-scale via Group scaleX/scaleY.
    cursors.push(
      <Group
        key={`${uid}-edge`}
        x={edgeSceneX}
        y={edgeSceneY}
        scaleX={1 / stage.scale}
        scaleY={1 / stage.scale}
        listening={false}
      >
        <EdgeIndicator
          name={c.displayName}
          color={c.color}
          dx={dx}
          dy={dy}
        />
      </Group>
    );
  }
  return <>{cursors}</>;
}

function hasSelectionPill(selection: CollaboratorState["selection"]): boolean {
  return !!(selection && (selection.furnitureId || selection.mountId || selection.openingId));
}

function CursorMarker({
  x,
  y,
  color,
  name,
  showLabel = true,
  scale = 1,
}: {
  x: number;
  y: number;
  color: string;
  name: string;
  showLabel?: boolean;
  /** Counter-scale for the Stage zoom so the cursor stays one size on screen. */
  scale?: number;
}) {
  // Cursor arrow inspired by the standard OS pointer.
  const arrow =
    "M 0 0 L 0 16 L 4 12 L 7 19 L 9 18 L 6 11 L 11 11 Z";
  const charW = 6.5;
  const padX = 6;
  const labelW = name.length * charW + padX * 2;
  return (
    <Group x={x} y={y} scaleX={scale} scaleY={scale} listening={false}>
      <Path
        data={arrow}
        fill={color}
        stroke="#ffffff"
        strokeWidth={1.5}
        shadowColor="#000"
        shadowBlur={3}
        shadowOpacity={0.25}
        shadowOffsetY={1}
      />
      {showLabel && (
        <Group x={14} y={14}>
          <Rect
            x={0}
            y={0}
            width={labelW}
            height={18}
            fill={color}
            cornerRadius={9}
          />
          <Text
            x={padX}
            y={3}
            text={name}
            fontSize={11}
            fontStyle="600"
            fontFamily="Inter, sans-serif"
            fill="#ffffff"
          />
        </Group>
      )}
    </Group>
  );
}

/**
 * Pill rendered at the viewport edge when a collaborator's cursor is
 * panned/zoomed off-screen. Includes a small chevron arrow on the side
 * that points toward the off-screen position so users can pan toward
 * a collaborator. Matches the same color/typography as the main pill.
 *
 * Receives `dx`/`dy` in *viewport* coordinates (cursor relative to
 * viewport center). The chevron is anchored on whichever side of the
 * pill faces the cursor.
 */
function EdgeIndicator({
  name,
  color,
  dx,
  dy,
}: {
  name: string;
  color: string;
  dx: number;
  dy: number;
}) {
  const charW = 6.5;
  const padX = 8;
  const labelW = name.length * charW + padX * 2 + 14; // +chevron gutter
  const labelH = 20;
  // Anchor the pill so the chevron points outward and the body sits
  // back toward the viewport center. We pick one of 4 anchor positions
  // (top/right/bottom/left) based on which axis has the larger delta.
  let anchorX = -labelW / 2;
  let anchorY = -labelH / 2;
  let chevron: "left" | "right" | "up" | "down" = "right";
  if (Math.abs(dx) > Math.abs(dy)) {
    if (dx > 0) {
      anchorX = -labelW;
      chevron = "right";
    } else {
      anchorX = 0;
      chevron = "left";
    }
  } else {
    if (dy > 0) {
      anchorY = -labelH;
      chevron = "down";
    } else {
      anchorY = 0;
      chevron = "up";
    }
  }
  // Chevron triangle paths anchored to the pill edge.
  const chevronPath = (() => {
    switch (chevron) {
      case "right":
        return `M ${labelW - 6} ${labelH / 2 - 4} L ${labelW - 2} ${labelH / 2} L ${labelW - 6} ${labelH / 2 + 4} Z`;
      case "left":
        return `M 6 ${labelH / 2 - 4} L 2 ${labelH / 2} L 6 ${labelH / 2 + 4} Z`;
      case "up":
        return `M ${labelW / 2 - 4} 6 L ${labelW / 2} 2 L ${labelW / 2 + 4} 6 Z`;
      case "down":
        return `M ${labelW / 2 - 4} ${labelH - 6} L ${labelW / 2} ${labelH - 2} L ${labelW / 2 + 4} ${labelH - 6} Z`;
    }
  })();
  return (
    <Group x={anchorX} y={anchorY} listening={false} opacity={0.92}>
      <Rect
        x={0}
        y={0}
        width={labelW}
        height={labelH}
        fill={color}
        cornerRadius={labelH / 2}
        shadowColor="#000"
        shadowBlur={4}
        shadowOpacity={0.2}
        shadowOffsetY={1}
      />
      <Path data={chevronPath} fill="#ffffff" />
      <Text
        x={chevron === "left" ? padX + 8 : padX}
        y={4}
        text={name}
        fontSize={11}
        fontStyle="600"
        fontFamily="Inter, sans-serif"
        fill="#ffffff"
      />
    </Group>
  );
}
